import type { User } from "@/types/api";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { type UserCreate, usersApi } from "./api";
import { usersKeys } from "./queryKeys";

interface Props {
  open: boolean;
  user: User | null;
  onClose: () => void;
}

const emptyForm: UserCreate = {
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  role: "user",
};

export function UserDialog({ open, user, onClose }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<UserCreate>(emptyForm);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        password: "",
        role: user.role ?? (user.owner ? "user" : "admin"),
      });
    } else {
      setForm(emptyForm);
    }
    setErr(null);
  }, [user, open]);

  const save = useMutation({
    mutationFn: () => user ? usersApi.update(user.id, form) : usersApi.create(form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.lists() });
      onClose();
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>User</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          required
          label="First Name"
          margin="normal"
          value={form.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
        />
        <TextField
          fullWidth
          required
          label="Last Name"
          margin="normal"
          value={form.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
        />
        <TextField
          fullWidth
          required
          type="email"
          label="Email"
          margin="normal"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <TextField
          fullWidth
          type="password"
          label={user ? "New Password (leave blank to keep)" : "Password"}
          margin="normal"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <RadioGroup
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as "admin" | "user" })}
          sx={{ mt: 1 }}
        >
          <FormControlLabel value="admin" control={<Radio />} label="Admin" />
          <FormControlLabel value="user" control={<Radio />} label="User" />
        </RadioGroup>
        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
